import React, { useState } from 'react';
import SEO from '../components/SEO';
import './Presupuesto.css';

const Presupuesto = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipoServicio: '',
    cantidad: '1',
    descripcion: '',
    honeypot: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formStartTime] = useState(Date.now());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection: Honeypot check
    if (formData.honeypot) return;
    
    // Bot protection: Minimum time to fill form (3 seconds)
    const timeToFill = Date.now() - formStartTime;
    if (timeToFill < 3000) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Por favor, espera unos segundos antes de enviar el formulario.' 
      });
      return;
    }
    
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';
    if (!formData.tipoServicio) newErrors.tipoServicio = 'Selecciona un tipo de servicio';
    if (!formData.descripcion.trim()) newErrors.descripcion = 'La descripción es obligatoria';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      
      if (data.success) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({ nombre: '', email: '', telefono: '', tipoServicio: '', cantidad: '1', descripcion: '', honeypot: '' });
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Error al enviar' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Error de conexión' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Solicitar Presupuesto - Artelaser3D",
    "description": "Solicita tu presupuesto gratis para grabado láser e impresión 3D en Asturias. Respuesta en 24 horas sin compromiso.",
    "url": "https://artelaser3d.es/presupuesto",
    "mainEntity": {
      "@type": "Service",
      "name": "Presupuesto Gratis - Grabado Láser e Impresión 3D",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Artelaser3D",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Asturias",
          "addressCountry": "ES"
        }
      },
      "areaServed": [
        { "@type": "City", "name": "Oviedo" },
        { "@type": "City", "name": "Gijón" },
        { "@type": "City", "name": "Avilés" },
        { "@type": "Country", "name": "España" }
      ],
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock"
      }
    }
  };

  return (
    <>
      <SEO 
        title="Solicitar Presupuesto"
        description="Solicita tu presupuesto gratis para grabado láser e impresión 3D en Asturias. Respuesta en 24 horas sin compromiso. Servicios personalizados para particulares y empresas."
        keywords="presupuesto grabado láser Asturias, presupuesto impresión 3D Asturias, presupuesto personalizado, presupuesto gratis Asturias"
        canonicalUrl="https://artelaser3d.es/presupuesto"
        schema={schema}
      />

      <div className="page-header presupuesto-header">
        <div className="container">
          <span className="badge">Presupuesto Gratis</span>
          <h1>Solicita tu <span className="text-accent">presupuesto</span></h1>
          <p>Completa el formulario y te enviaremos un presupuesto personalizado en menos de 24 horas.</p>
        </div>
      </div>

      <section className="form-section">
        <div className="container">
          <div className="form-grid">
            <div className="form-box">
              <form onSubmit={handleSubmit} className="presupuesto-form">
                <div className="form-group">
                  <label>Nombre completo *</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                  />
                  {errors.nombre && <span className="error">{errors.nombre}</span>}
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                  />
                  {errors.email && <span className="error">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+34 XXX XXX XXX"
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de servicio *</label>
                  <select name="tipoServicio" value={formData.tipoServicio} onChange={handleChange}>
                    <option value="">Selecciona un servicio</option>
                    <option value="grabado-laser">Grabado Láser</option>
                    <option value="impresion-3d">Impresión 3D</option>
                    <option value="ambos">Ambos servicios</option>
                  </select>
                  {errors.tipoServicio && <span className="error">{errors.tipoServicio}</span>}
                </div>

                <div className="form-group">
                  <label>Cantidad</label>
                  <input
                    type="number"
                    name="cantidad"
                    value={formData.cantidad}
                    onChange={handleChange}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Descripción del proyecto *</label>
                  <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Cuéntanos los detalles de tu proyecto..."
                    rows="5"
                  />
                  {errors.descripcion && <span className="error">{errors.descripcion}</span>}
                </div>

                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                />

                {submitStatus && (
                  <div className={`status-message ${submitStatus.type}`}>
                    {submitStatus.message}
                  </div>
                )}

                <button type="submit" disabled={isSubmitting} className="submit-btn">
                  {isSubmitting ? 'Enviando...' : 'Solicitar Presupuesto'}
                </button>
              </form>
            </div>

            <div className="info-box">
              <h3>¿Qué incluye el presupuesto?</h3>
              <ul>
                <li>✓ Análisis detallado de tu proyecto</li>
                <li>✓ Recomendación de materiales</li>
                <li>✓ Desglose de costes transparente</li>
                <li>✓ Plazo de entrega estimado</li>
                <li>✓ Sin compromiso</li>
              </ul>
              <div className="tip">
                <strong>💡 Tip:</strong> Cuanta más información nos proporciones, más preciso será el presupuesto.
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Presupuesto;
